import * as ICAL from 'ical.js'
import * as moment from 'moment'
import { Calendar } from '../model/Calendar'
import { RoomRule } from '../model/Calendar/RoomRule'
import { Device } from '../model/Device'
import { Room as RoomModel } from '../model/Device/Room'
import { Route as RouteModel } from '../model/Device/Router/Route'
import { Event } from '../model/Event'
import { Room } from '../model/Event/Room'
import { Route } from '../model/Event/Room/Route'
import { ISettings } from './hashParser'

interface ICALEventWithDetails {
  event: ICAL.Event
  recur: ICAL.Recur|null
  attachment: string|null
}

interface IOccurrence {
  uniqueId: string
  startDate: Date
  endDate: Date
}

const getComponent = (iCalData: string): ICAL.Component => {
  const jCalData = ICAL.parse(iCalData)

  return new ICAL.Component(jCalData)
}

const filterRoomRulesForDevice = (roomRules: RoomRule[], device: Device) => {
  const deviceRooms: string[] = device.rooms.map((room: RoomModel) => room.id)
  const deviceRoomRules = roomRules.filter((roomRule: RoomRule) => deviceRooms.includes(roomRule.roomId))

  if (device.router) {
    const routerRooms: string[] = device.router.routes.map((route: RouteModel) => route.room.id)
    const routerRoomRules = roomRules.filter((roomRule: RoomRule) => routerRooms.includes(roomRule.roomId))

    return [...deviceRoomRules, ...routerRoomRules]
  }

  return deviceRoomRules
}

const getRecur = (vevent: ICAL.Component): ICAL.Recur|null => {
  const rrule = vevent.getFirstPropertyValue('rrule')

  if (!rrule) {
    return null
  }

  return rrule
}

const getAttachment = (vevent: ICAL.Component): string|null => {
  const attach = vevent.getFirstPropertyValue('attach')

  if (!attach) {
    return null
  }

  return attach
}

const getRoom = (roomRules: RoomRule[], device: Device, event: ICAL.Event) => {
  let room: Room | null = null

  roomRules.forEach((roomRule: RoomRule) => {
    if (room) {
      return
    }

    if (!new RegExp(roomRule.locationRegEx).test(event.location)) {
      return
    }

    if (device.router) {
      const modelRoute: RouteModel = device.router.routes.find((route: RouteModel) => route.room.id === roomRule.roomId)

      if (modelRoute) {
        const deviceRoom: RoomModel = device.rooms.find((r: RoomModel) => r.id === roomRule.roomId)

        if (!deviceRoom) {
          room = new Room(
            modelRoute.room.id,
            modelRoute.room.name,
            modelRoute.room.door,
            modelRoute.room.floor,
            new Route(
              device.router.id,
              modelRoute.distance,
              modelRoute.direction,
            ),
          )

          return
        }
      }
    }

    const modelRoom: RoomModel = device.rooms.find((r: RoomModel) => r.id === roomRule.roomId)

    if (modelRoom) {
      room = new Room(
        modelRoom.id,
        modelRoom.name,
        modelRoom.door,
        modelRoom.floor,
      )
    }
  })

  return room
}

const performEventChecks = (event: ICAL.Event): void => {
  const { uid, startDate, endDate } = event

  if (!uid) {
    console.error('Event is missing uid.')
  }

  if (!startDate) {
    console.error(`Event with uid '${uid}' is missing start date.`)
  }

  if (!endDate) {
    console.error(`Event with uid '${uid}' is missing end date.`)
  }
}

const findOccurrences = (
  periodFromDate: Date,
  periodToDate: Date,
  event: ICAL.Event,
  recur: ICAL.Recur|null,
): IOccurrence[] => {
  const occurrences: IOccurrence[] = []
  const { uid, sequence } = event
  const currentDateUnix = new Date().getTime()
  const periodFromDateUnix = periodFromDate.getTime()
  const periodToDateUnix = periodToDate.getTime()
  const eventStartDate = event.startDate.toJSDate()
  const eventEndDate = event.endDate.toJSDate()

  if (recur) {
    const iterator = recur.iterator(event.startDate)

    for (let next = iterator.next(); next; next = iterator.next()) {
      const details = event.getOccurrenceDetails(next)
      const startDateUnix = details.startDate.toJSDate().getTime()
      const endDateUnix = details.endDate.toJSDate().getTime()

      if (
        endDateUnix > periodFromDateUnix
        && startDateUnix < periodToDateUnix
        && endDateUnix > currentDateUnix
        && !details.item.findRangeException(details.startDate)
      ) {
        occurrences.push({
          uniqueId: `${uid}-${sequence}-${details.recurrenceId.toJSDate().getTime()}`,
          startDate: details.startDate.toJSDate(),
          endDate: details.endDate.toJSDate(),
        })
      }

      if (startDateUnix > periodToDateUnix) {
        break
      }
    }
  } else if (
    eventEndDate.getTime() > periodFromDateUnix
    && eventStartDate.getTime() < periodToDateUnix
    && eventEndDate.getTime() > currentDateUnix
  ) {
    occurrences.push(
      {
        uniqueId: `${uid}-${sequence}-${eventStartDate.getTime()}`,
        startDate: eventStartDate,
        endDate: eventEndDate,
      },
    )
  }

  return occurrences
}

const formatDescription = (description: string): string => description
  .replace('\n', '<br>') // eslint-disable-next-line
  .replace('\,', ',') // marked as unnecessary es18:15cape character

const areSameDayDates = (date1: Date, date2: Date): boolean => date1.getFullYear() === date2.getFullYear()
  && date1.getMonth() === date2.getMonth()
  && date1.getDate() === date2.getDate()

const removeSequenceIdDuplicates = (events: Event[]): Event[] => {
  events.forEach((event: Event) => {
    const duplicates = events.filter(
      (filteredEvent: Event) => filteredEvent.uid === event.uid && filteredEvent.key !== event.key,
    )

    if (duplicates.length === 0) {
      return
    }

    duplicates.forEach((duplicate: Event) => {
      if (
        areSameDayDates(duplicate.fromDate, event.fromDate)
        && (duplicate.sequence < event.sequence
        || (duplicate.sequence === event.sequence && !duplicate.recurrenceId))
      ) {
        events = events.filter((filteredEvent: Event) => filteredEvent.key !== duplicate.key)
      }
    })
  })

  return events
}

export const parseCalendar = (
  calendar: Calendar,
  eventInterval: number,
  settings: ISettings,
  device: Device,
  iCalData: string,
): Event[] => {
  const component = getComponent(iCalData)
  const periodFromDate: Date = moment().subtract(168, 'hours').toDate()
  const periodToDate: Date = moment().add(eventInterval, 'hours').toDate()
  const roomRules = filterRoomRulesForDevice(calendar.roomRules, device).sort( // ".*" regex should be the last one rule
    (a: RoomRule, b: RoomRule) => b.locationRegEx.length - a.locationRegEx.length,
  )
  const parsedEvents: Event[] = []

  const iCalEvents: ICALEventWithDetails[] = component
    .getAllSubcomponents('vevent')
    .map((vevent: ICAL.Component) => ({
      event: new ICAL.Event(vevent),
      recur: getRecur(vevent),
      attachment: getAttachment(vevent),
    }))

  iCalEvents.forEach((ievent: ICALEventWithDetails) => {
    const { event, attachment, recur } = ievent
    const { uid, sequence, recurrenceId, description, summary, location } = event
    const room: Room|null = getRoom(roomRules, device, event)

    if (!room) {
      return
    }

    performEventChecks(event)

    const occurrences = findOccurrences(periodFromDate, periodToDate, event, recur)

    occurrences.forEach((occurrence: IOccurrence) => {
      parsedEvents.push(new Event(
        occurrence.uniqueId,
        uid,
        occurrence.startDate,
        occurrence.endDate,
        sequence,
        attachment,
        room,
        formatDescription(description),
        summary,
        location,
        recurrenceId ? recurrenceId.toJSDate() : null,
      ))
    })
  })

  return removeSequenceIdDuplicates(parsedEvents)
  // return parsedEvents
}
